import React, { useState, useEffect } from 'react'
import { FaImage, FaSave, FaPlus, FaTrash, FaUndo, FaEye, FaVideo } from 'react-icons/fa'
import settingsService from '../services/settingsService'
import { toast } from 'react-toastify'
import Loader from './Loader'

const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.mov']
const isVideoUrl = (url) => {
  if (!url) return false
  try {
    const pathname = new URL(url).pathname.toLowerCase()
    return VIDEO_EXTENSIONS.some((ext) => pathname.endsWith(ext))
  } catch {
    return VIDEO_EXTENSIONS.some((ext) => url.toLowerCase().endsWith(ext))
  }
}

const HeroSliderSettings = () => {
  const [settings, setSettings] = useState({
    hero_slider_images: [],
    hero_title: 'Your Perfect Himalayan Adventure, Planned by Local Experts',
    hero_subtitle:
      'Curated treks, heritage tours & adventure activities across Himachal Pradesh — guided by people who grew up in these mountains.',
    hero_slider_interval: 5000,
    hero_slider_animation: 'fade',
    hero_cta_primary_text: 'Find Your Perfect Trek',
    hero_cta_primary_link: '/tours',
    hero_cta_secondary_text: 'Build a Custom Itinerary',
    hero_cta_secondary_link: '/tour-planner',
  })
  const [newImageUrl, setNewImageUrl] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [previewIndex, setPreviewIndex] = useState(0)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      const response = await settingsService.getPublicSettings()
      if (response.success && response.settings) {
        setSettings({
          hero_slider_images: response.settings.hero_slider_images || [],
          hero_title:
            response.settings.hero_title ||
            'Your Perfect Himalayan Adventure, Planned by Local Experts',
          hero_subtitle:
            response.settings.hero_subtitle ||
            'Curated treks, heritage tours & adventure activities across Himachal Pradesh — guided by people who grew up in these mountains.',
          hero_slider_interval: parseInt(response.settings.hero_slider_interval) || 5000,
          hero_slider_animation: response.settings.hero_slider_animation || 'fade',
          hero_cta_primary_text:
            response.settings.hero_cta_primary_text || 'Find Your Perfect Trek',
          hero_cta_primary_link: response.settings.hero_cta_primary_link || '/tours',
          hero_cta_secondary_text:
            response.settings.hero_cta_secondary_text || 'Build a Custom Itinerary',
          hero_cta_secondary_link: response.settings.hero_cta_secondary_link || '/tour-planner',
        })
      }
    } catch (error) {
      toast.error('Failed to load hero slider settings')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setSettings((prev) => ({ ...prev, [name]: value }))
  }

  const addImage = () => {
    if (!newImageUrl.trim()) {
      toast.warning('Please enter an image URL')
      return
    }

    // Basic URL validation
    try {
      new URL(newImageUrl)
    } catch {
      toast.error('Please enter a valid URL')
      return
    }

    setSettings((prev) => ({
      ...prev,
      hero_slider_images: [...prev.hero_slider_images, newImageUrl.trim()],
    }))
    setNewImageUrl('')
    toast.success('Image added! Remember to save changes.')
  }

  const removeImage = (index) => {
    setSettings((prev) => ({
      ...prev,
      hero_slider_images: prev.hero_slider_images.filter((_, i) => i !== index),
    }))
    toast.info('Image removed! Remember to save changes.')
  }

  const moveImage = (index, direction) => {
    const newImages = [...settings.hero_slider_images]
    const newIndex = direction === 'up' ? index - 1 : index + 1

    if (newIndex < 0 || newIndex >= newImages.length) return
    ;[newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]]

    setSettings((prev) => ({
      ...prev,
      hero_slider_images: newImages,
    }))
  }

  const handleSave = async () => {
    if (settings.hero_slider_images.length === 0) {
      toast.error('Please add at least one image')
      return
    }

    setSaving(true)
    try {
      // Update each setting
      await settingsService.updateSetting(
        'hero_slider_images',
        JSON.stringify(settings.hero_slider_images),
      )
      await settingsService.updateSetting('hero_title', settings.hero_title)
      await settingsService.updateSetting('hero_subtitle', settings.hero_subtitle)
      await settingsService.updateSetting(
        'hero_slider_interval',
        settings.hero_slider_interval.toString(),
      )
      await settingsService.updateSetting('hero_slider_animation', settings.hero_slider_animation)
      await settingsService.updateSetting('hero_cta_primary_text', settings.hero_cta_primary_text)
      await settingsService.updateSetting('hero_cta_primary_link', settings.hero_cta_primary_link)
      await settingsService.updateSetting(
        'hero_cta_secondary_text',
        settings.hero_cta_secondary_text,
      )
      await settingsService.updateSetting(
        'hero_cta_secondary_link',
        settings.hero_cta_secondary_link,
      )

      toast.success('Hero slider settings saved successfully!')
    } catch (error) {
      toast.error('Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    loadSettings()
    toast.info('Settings reset to last saved values')
  }

  if (loading) {
    return <Loader />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FaImage className="text-blue-600" />
            Hero Slider Settings
          </h2>
          <p className="text-gray-600 mt-1">Manage hero section images and content</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
          >
            <FaUndo /> Reset
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            <FaSave /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Hero Content Settings */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Hero Content</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Main Title</label>
            <input
              type="text"
              name="hero_title"
              value={settings.hero_title}
              onChange={handleInputChange}
              placeholder="Your Perfect Himalayan Adventure, Planned by Local Experts"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
            <input
              type="text"
              name="hero_subtitle"
              value={settings.hero_subtitle}
              onChange={handleInputChange}
              placeholder="Curated treks, heritage tours & adventure activities..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Slider Images */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Background Media</h3>

        {/* Add New Media */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Add New Image or Video
          </label>
          <div className="flex gap-2">
            <input
              type="url"
              value={newImageUrl}
              onChange={(e) => setNewImageUrl(e.target.value)}
              placeholder="https://example.com/image.jpg or video.mp4"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              onKeyPress={(e) => e.key === 'Enter' && addImage()}
            />
            <button onClick={addImage} className="btn-success flex items-center gap-2">
              <FaPlus /> Add
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Images: 1920x1080+, use Unsplash/Pexels. Videos: .mp4, .webm, .ogg — auto-detected by
            extension.
          </p>
        </div>

        {/* Media List */}
        {settings.hero_slider_images.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <FaImage className="text-6xl text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              No media added yet. Add your first image or video above.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {settings.hero_slider_images.map((media, index) => (
              <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                {/* Media Preview */}
                <div className="relative w-32 h-20 flex-shrink-0">
                  <img
                    src={isVideoUrl(media) ? '' : media}
                    alt={`Slide ${index + 1}`}
                    className="w-full h-full object-cover rounded"
                    onError={(e) => {
                      e.target.src = 'https://placehold.co/320x180/cccccc/666666?text=Invalid+Image'
                    }}
                    style={isVideoUrl(media) ? { display: 'none' } : {}}
                  />
                  {isVideoUrl(media) && (
                    <div className="w-full h-full bg-gray-800 rounded flex items-center justify-center">
                      <FaVideo className="text-white text-2xl" />
                    </div>
                  )}
                  <div className="absolute top-1 left-1 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                    #{index + 1} {isVideoUrl(media) ? '🎬' : '🖼️'}
                  </div>
                </div>

                {/* URL Display */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-600 truncate font-mono">{media}</p>
                  {isVideoUrl(media) && (
                    <span className="text-xs text-blue-600 font-semibold">Video</span>
                  )}
                </div>

                {/* Controls */}
                <div className="flex gap-2">
                  <button
                    onClick={() => moveImage(index, 'up')}
                    disabled={index === 0}
                    className="p-2 text-gray-600 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move up"
                  >
                    ▲
                  </button>
                  <button
                    onClick={() => moveImage(index, 'down')}
                    disabled={index === settings.hero_slider_images.length - 1}
                    className="p-2 text-gray-600 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move down"
                  >
                    ▼
                  </button>
                  <button
                    onClick={() => removeImage(index)}
                    className="p-2 text-red-600 hover:text-red-700"
                    title="Remove"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Slider Settings */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Slider Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Transition Interval (seconds)
            </label>
            <input
              type="number"
              name="hero_slider_interval"
              value={settings.hero_slider_interval / 1000}
              onChange={(e) =>
                setSettings((prev) => ({
                  ...prev,
                  hero_slider_interval: parseInt(e.target.value) * 1000,
                }))
              }
              min="2"
              max="10"
              step="0.5"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">How long each image is displayed</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Animation Type</label>
            <select
              name="hero_slider_animation"
              value={settings.hero_slider_animation}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="fade">Fade</option>
              <option value="slide">Slide</option>
            </select>
          </div>
        </div>
      </div>

      {/* CTA Buttons */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">CTA Buttons</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Primary Button Text
            </label>
            <input
              type="text"
              name="hero_cta_primary_text"
              value={settings.hero_cta_primary_text}
              onChange={handleInputChange}
              placeholder="Explore Tours"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Primary Button Link
            </label>
            <input
              type="text"
              name="hero_cta_primary_link"
              value={settings.hero_cta_primary_link}
              onChange={handleInputChange}
              placeholder="/tours"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Secondary Button Text
            </label>
            <input
              type="text"
              name="hero_cta_secondary_text"
              value={settings.hero_cta_secondary_text}
              onChange={handleInputChange}
              placeholder="Plan Your Trip"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Secondary Button Link
            </label>
            <input
              type="text"
              name="hero_cta_secondary_link"
              value={settings.hero_cta_secondary_link}
              onChange={handleInputChange}
              placeholder="/tour-planner"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Preview */}
      {settings.hero_slider_images.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FaEye /> Preview
          </h3>
          <div className="relative h-64 rounded-lg overflow-hidden">
            {isVideoUrl(settings.hero_slider_images[previewIndex]) ? (
              <video
                src={settings.hero_slider_images[previewIndex]}
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
              />
            ) : (
              <img
                src={settings.hero_slider_images[previewIndex]}
                alt="Preview"
                className="w-full h-full object-cover"
              />
            )}
            <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
              <div className="text-center text-white">
                <h2 className="text-3xl font-bold mb-2">{settings.hero_title}</h2>
                <p className="text-lg">{settings.hero_subtitle}</p>
                <div className="flex gap-3 justify-center mt-3">
                  <span className="bg-green-600 px-4 py-2 rounded-lg text-sm font-semibold">
                    {settings.hero_cta_primary_text || 'Explore Tours'}
                  </span>
                  <span className="border border-white px-4 py-2 rounded-lg text-sm font-semibold">
                    {settings.hero_cta_secondary_text || 'Plan Your Trip'}
                  </span>
                </div>
              </div>
            </div>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {settings.hero_slider_images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setPreviewIndex(index)}
                  className={`w-2 h-2 rounded-full transition ${
                    index === previewIndex ? 'bg-white w-8' : 'bg-white bg-opacity-50'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default HeroSliderSettings
